/* ==========================================================================
   MEE BARSA — Product Strip, scroll-driven motion
   Option 18 (finalised) — replaces the 60s CSS keyframe marquee.

   Why: the old marquee ran on a timer, so it moved while people were trying
   to look at it, looped past things they wanted, and — because the track was
   `width:max-content` with no container — cut both edges at whatever point
   the clock happened to be at, which is what made the block read as broken.

   Here the track's horizontal position is a pure function of the section's
   position in the viewport: it advances as you scroll down and stops dead the
   instant you stop. No timer, nothing running in a background tab.

   Accessibility: visible previous/next controls make every product reachable
   without requiring page movement. prefers-reduced-motion turns the rail into
   a native horizontal scroller while retaining the same controls.
   If this script never runs, the track simply sits at rest under the edge
   masks — degraded to a static row, never broken.

   2026-08-07 — THE CONTROLS ARE NOW HIDDEN BY THE DESIGN PASS, so the
   paragraph above is no longer the whole story: hand drag (added below) is
   what actually makes every product reachable. See the long note there.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__mbStripGlobalBound) return;
  window.__mbStripGlobalBound = true;

  var reduce = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(section) {
    if (!section || section.__mbStripBound) return;
    section.__mbStripBound = true;

    var rail = section.querySelector('.mb-strip-rail');
    var track = section.querySelector('.mb-strip');
    var controls = section.querySelector('.mb-strip-controls');
    var prev = section.querySelector('[data-mb-strip-prev]');
    var next = section.querySelector('[data-mb-strip-next]');
    if (!rail || !track) {
      section.__mbStripBound = false;
      return;
    }

    if (controls) controls.hidden = false;

    if (reduce) {
      section.classList.add('mb-strip-static');

      function updateNativeControls() {
        var maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
        if (prev) prev.disabled = rail.scrollLeft <= 1;
        if (next) next.disabled = maxScroll <= 0 || rail.scrollLeft >= maxScroll - 1;
      }

      function nativeMove(direction) {
        var step = Math.max(240, rail.clientWidth * .72);
        rail.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      function onPrevClick() { nativeMove(-1); }
      function onNextClick() { nativeMove(1); }

      if (prev) prev.addEventListener('click', onPrevClick);
      if (next) next.addEventListener('click', onNextClick);
      rail.addEventListener('scroll', updateNativeControls, { passive: true });
      section.__mbStripCleanup = function () {
        if (prev) prev.removeEventListener('click', onPrevClick);
        if (next) next.removeEventListener('click', onNextClick);
        rail.removeEventListener('scroll', updateNativeControls);
        section.__mbStripBound = false;
        delete section.__mbStripCleanup;
      };
      updateNativeControls();
      return;
    }

    var max = 0;
    var ticking = false;
    var visible = true;
    var baseX = 0;
    var manualBias = 0;
    var currentX = 0;

    function clamp(value, low, high) {
      return Math.max(low, Math.min(high, value));
    }

    function updateControls() {
      if (prev) prev.disabled = currentX >= -.5;
      if (next) next.disabled = max <= 0 || currentX <= -max + .5;
    }

    function measure() {
      // How far the track can travel before its right edge is flush.
      max = Math.max(0, track.scrollWidth - rail.clientWidth);
      manualBias = clamp(manualBias, -max - baseX, -baseX);
    }

    function paint() {
      ticking = false;
      var r = section.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      /* Keep the response proportional to the visitor's gesture. Mapping the
         full, twenty-card track across one section made a small vertical move
         throw the rail thousands of pixels sideways. A restrained 0.22:1
         ratio shifts the composition gently; arrows still reach every piece. */
      var entered = Math.max(0, vh - r.top);
      baseX = -Math.min(max, entered * .22);
      currentX = clamp(baseX + manualBias, -max, 0);
      track.style.transform = 'translate3d(' + currentX.toFixed(2) + 'px,0,0)';
      updateControls();
    }

    function move(direction) {
      measure();
      var step = Math.max(240, Math.min(rail.clientWidth * .72, 720));
      currentX = clamp(currentX + direction * step, -max, 0);
      manualBias = currentX - baseX;
      track.style.transform = 'translate3d(' + currentX.toFixed(2) + 'px,0,0)';
      updateControls();
    }

    function onScroll() {
      if (!ticking && visible) {
        ticking = true;
        window.requestAnimationFrame(paint);
      }
    }

    var resizeTimer;
    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        measure();
        paint();
      }, 120);
    }

    // Don't do any work while the section is nowhere near the viewport.
    var observer = null;
    if ('IntersectionObserver' in window) {
      visible = false;
      observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) { measure(); paint(); }
      }, { rootMargin: '200px 0px' });
      observer.observe(section);
    }

    /* ----------------------------------------------------------------------
       HAND DRAG — added 2026-08-07.

       WHY IT WAS NEEDED. The Aug-5 design pass removed the two arrow discs on
       the stated reasoning that the rail "already advances on scroll and drags
       by hand". It advanced on scroll; it had never dragged by hand — no such
       code has ever existed in this file — and .mb-strip-rail is
       overflow:hidden, so there was no native swipe to fall back on either.
       That left the 0.22:1 scroll gearing as the only mover. Measured on the
       draft before this fix, the furthest the rail could EVER be pushed was
       399px of 4212px of travel at 1440 (9.5%) and 329px of 2500px at 390
       (13.2%) — about 4 of the 10 pieces on a desktop and 2 of 10 on a phone.
       The rest of the rail was unreachable by any gesture.

       HOW IT WORKS. Drag writes to `manualBias`, the same offset the arrows
       used, so hand movement COMPOSES with the scroll drift rather than
       fighting it: paint() keeps recomputing baseX from scroll position and
       adds the bias on top. Nothing about the drift changes.

       THE AXIS LOCK IS THE PART THAT MATTERS ON A PHONE. Until a gesture has
       travelled 4px we do not know whether it is a swipe along the rail or the
       visitor scrolling the page through it. Guess wrong and either the rail
       ignores swipes or the page stops scrolling wherever the rail happens to
       be. So the first 4px decide, once, and a vertical verdict releases the
       gesture back to the browser for good. Paired with touch-action:pan-y in
       the stylesheet, which stops the browser cancelling the pointer stream
       before we get to make that decision.
       ---------------------------------------------------------------------- */
    var dragging = false, dragId = null, axis = null;
    var startX = 0, startY = 0, startBias = 0, movedBy = 0;
    var vx = 0, lastX = 0, lastT = 0, glideRaf = 0;

    function applyX(x) {
      currentX = clamp(x, -max, 0);
      manualBias = currentX - baseX;
      track.style.transform = 'translate3d(' + currentX.toFixed(2) + 'px,0,0)';
      updateControls();
    }

    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      if (glideRaf) { window.cancelAnimationFrame(glideRaf); glideRaf = 0; }
      measure();
      if (max <= 0) return;
      dragging = true; dragId = e.pointerId; axis = null; movedBy = 0; vx = 0;
      startX = lastX = e.clientX; startY = e.clientY;
      startBias = manualBias;
      lastT = e.timeStamp || Date.now();
    }

    function onPointerMove(e) {
      if (!dragging || e.pointerId !== dragId) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') { dragging = false; dragId = null; return; }
        section.classList.add('is-dragging');
        if (rail.setPointerCapture) { try { rail.setPointerCapture(dragId); } catch (err) {} }
      }
      var t = e.timeStamp || Date.now(), dt = t - lastT;
      if (dt > 0) { vx = (e.clientX - lastX) / dt; lastT = t; lastX = e.clientX; }
      movedBy = Math.abs(dx);
      applyX(baseX + startBias + dx);
      if (e.cancelable) e.preventDefault();
    }

    /* A short coast after release. Without it a flick stops dead under the
       finger, which on a rail this long feels like the thing is stuck again. */
    function glide() {
      vx *= 0.94;
      if (Math.abs(vx) < 0.02) { glideRaf = 0; return; }
      applyX(currentX + vx * 16);
      glideRaf = window.requestAnimationFrame(glide);
    }

    function onPointerUp(e) {
      if (!dragging || (dragId !== null && e.pointerId !== dragId)) return;
      dragging = false;
      if (axis === 'x') {
        section.classList.remove('is-dragging');
        if (rail.releasePointerCapture) { try { rail.releasePointerCapture(dragId); } catch (err) {} }
        if (Math.abs(vx) > 0.05) glideRaf = window.requestAnimationFrame(glide);
      }
      dragId = null;
    }

    /* A drag that finishes on a photograph must not also open that product. */
    function onClickCapture(e) {
      if (movedBy > 6) { e.preventDefault(); e.stopPropagation(); }
      movedBy = 0;
    }
    function onDragStart(e) { e.preventDefault(); }

    /* Keyboard reachability. With the arrows gone, tabbing was the one route
       left that could move focus to a card sitting outside the mask, where the
       focus ring is invisible. Bring whatever gains focus into the frame. */
    function onFocusIn(e) {
      var a = e.target && e.target.closest ? e.target.closest('.mb-strip > a') : null;
      if (!a) return;
      measure();
      var ar = a.getBoundingClientRect(), rr = rail.getBoundingClientRect(), d = 0;
      if (ar.left < rr.left + 8) d = (rr.left + 8) - ar.left;
      else if (ar.right > rr.right - 8) d = (rr.right - 8) - ar.right;
      if (d) applyX(currentX + d);
    }

    rail.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    rail.addEventListener('click', onClickCapture, true);
    rail.addEventListener('dragstart', onDragStart);
    rail.addEventListener('focusin', onFocusIn);

    measure();
    paint();

    var driftRaf = 0, driftLast = 0, driftDirection = -1, driftPaused = false;
    var driftToggle = document.createElement('button');
    driftToggle.type = 'button';
    driftToggle.className = 'mb-strip-motion-toggle';
    driftToggle.textContent = 'Pause movement';
    driftToggle.setAttribute('aria-pressed', 'false');
    rail.insertAdjacentElement('afterend', driftToggle);
    function toggleDrift() {
      driftPaused = !driftPaused;
      driftToggle.textContent = driftPaused ? 'Resume movement' : 'Pause movement';
      driftToggle.setAttribute('aria-pressed', String(driftPaused));
    }
    driftToggle.addEventListener('click', toggleDrift);
    function drift(now) {
      var dt = driftLast ? Math.min(50, now - driftLast) : 0;
      driftLast = now;
      var reducedNow = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var hovered = window.matchMedia('(hover:hover)').matches && section.matches(':hover');
      if (visible && !document.hidden && !driftPaused && !reducedNow && !hovered && !section.contains(document.activeElement) && !dragging && !glideRaf && max > 0) {
        if (currentX <= -max + .5) driftDirection = 1;
        if (currentX >= -.5) driftDirection = -1;
        applyX(currentX + driftDirection * dt * .008);
      }
      driftRaf = window.requestAnimationFrame(drift);
    }
    driftRaf = window.requestAnimationFrame(drift);

    function onPrevClick() { move(1); }
    function onNextClick() { move(-1); }
    function onLoad() { measure(); paint(); }

    if (prev) prev.addEventListener('click', onPrevClick);
    if (next) next.addEventListener('click', onNextClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onLoad);

    section.__mbStripCleanup = function () {
      window.cancelAnimationFrame(driftRaf);
      driftToggle.removeEventListener('click', toggleDrift);
      driftToggle.remove();
      if (prev) prev.removeEventListener('click', onPrevClick);
      if (next) next.removeEventListener('click', onNextClick);
      rail.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      rail.removeEventListener('click', onClickCapture, true);
      rail.removeEventListener('dragstart', onDragStart);
      rail.removeEventListener('focusin', onFocusIn);
      if (glideRaf) window.cancelAnimationFrame(glideRaf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onLoad);
      window.clearTimeout(resizeTimer);
      if (observer) observer.disconnect();
      section.__mbStripBound = false;
      delete section.__mbStripCleanup;
    };
  }

  function boot(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return;
    var nodes = Array.prototype.slice.call(scope.querySelectorAll('.mb-strip-section'));
    if (scope.matches && scope.matches('.mb-strip-section')) nodes.unshift(scope);
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target;
    if (!root || !root.querySelectorAll) return;
    var nodes = Array.prototype.slice.call(root.querySelectorAll('.mb-strip-section'));
    if (root.matches && root.matches('.mb-strip-section')) nodes.unshift(root);
    for (var i = 0; i < nodes.length; i++) {
      if (typeof nodes[i].__mbStripCleanup === 'function') {
        nodes[i].__mbStripCleanup();
      }
    }
  });
})();
